import { AgreementType } from '../types/Agreement.ts'
import { valueLookup } from '../types/dictionary.ts'
import { jobTypesList } from '../types/education.ts'
import type { Job } from '../types/Job.ts'
import { JobFeeType, JobType } from '../types/Job.ts'
import type { Org } from '../types/Org.ts'
import { OrgType } from '../types/Org.ts'

export const formatJobType = (job?: Pick<Job, 'type'> & { type?: JobType | JobType[] }): string => {
    if (!job?.type) return ''

    if (Array.isArray(job.type)) {
        return job.type
            .map((type) => valueLookup(type, jobTypesList))
            ?.filter(Boolean)
            ?.join(', ')
    }

    // Deprecated: JobType is now replaced by JobType[]
    return valueLookup(job.type, jobTypesList) || ''
}

export const formatJobLocation = (job?: Pick<Job, 'jobLocation'>): string => {
    if (!job?.jobLocation) return ''

    return job.jobLocation?.filter(Boolean)?.join(', ')
}

export const historicJobAdapter = (job: Job & { type?: JobType | JobType[] }): Job => {
    if (!job.type) return { ...job, type: [] }

    if (!Array.isArray(job.type)) {
        return { ...job, type: [job.type] }
    }

    return job
}

export const isConsentRequired = (
    agreementType: AgreementType,
    org?: Partial<Pick<Org, 'type'>>,
    job?: Partial<Pick<Job, 'type' | 'feeType'>>
): boolean => {
    if (!job || !org) return false

    const jobFee = job.feeType ?? JobFeeType.SATISFACTION

    switch (agreementType) {
        case AgreementType.jobMediationContract: {
            if (!job.type || !org.type) return false
            const isCorrectOrgType = [OrgType.privateSector, OrgType.startUp].includes(org.type)
            const isCorrectJobType = job.type.includes(JobType.partTime) || job.type.includes(JobType.fullTime)
            const isCorrectJobFee = jobFee === JobFeeType.SATISFACTION
            return isCorrectJobFee && isCorrectOrgType && isCorrectJobType
        }
        case AgreementType.internshipMediationContract: {
            if (!job.type || !org.type) return false
            const isCorrectOrgType = [OrgType.privateSector, OrgType.startUp].includes(org.type)
            const isCorrectJobType = job.type.includes(JobType.internship)
            const isCorrectJobFee = jobFee === JobFeeType.SATISFACTION
            return isCorrectJobFee && isCorrectOrgType && isCorrectJobType
        }
        case AgreementType.jobAdvertisingTermsAndConditions: {
            if (!org.type || !job.feeType) return false
            const isCorrectOrgType = [OrgType.privateSector, OrgType.startUp].includes(org.type)
            const isCorrectJobFee = job.feeType === JobFeeType.ADVERTISING
            return isCorrectOrgType && isCorrectJobFee
        }
        default:
            return false
    }
}
